import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectLookerReports, selectReportSliceStatus } from "redux/slices/clientLens";
import ReportListItem from "./ReportListItem";
import RequestStatus from "../../../../redux/slices/RequestStatus";
import { ExpandMore, Folder, FolderOpen } from "@mui/icons-material";
import theme from "theme";
import { LookerFolderView } from "generated/graphql";
import { useNavigate } from "react-router";

interface LensAdminReportsProps {
  lensId: number;
}

interface ReportFolderProps {
  folder: LookerFolderView;
  lensId: number;
}

function ReportFolder({ folder, lensId }: ReportFolderProps) {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const toggleOpen = () => {
    setOpen(!open);
  };

  return (
    <ListItem
      sx={{
        paddingLeft: 0,
        paddingRight: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
      }}>
      <ListItemButton
        onClick={toggleOpen}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          py: 2,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}>
          <ListItemIcon
            sx={{
              height: 34,
              width: 34,
              minWidth: 34,
              marginRight: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[100],
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            {open ? <FolderOpen /> : <Folder />}
          </ListItemIcon>
          <ListItemText primary={folder.name} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            height: "100%",
          }}>
          <IconButton edge="end" onClick={toggleOpen}>
            <ExpandMore
              sx={{
                transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </IconButton>
        </Box>
      </ListItemButton>
      <Collapse
        in={open}
        sx={{
          width: "100%",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            ml: 4,
            pb: 2,
          }}>
          <Box
            sx={{
              borderLeft: `2px solid ${theme.palette.grey[100]}`,
            }}>
            {folder.embedItems?.map((item) => (
              <ReportListItem
                onClick={() => {
                  navigate(`/lens/${lensId}/report/${item.id}`);
                }}
                key={item.url}
                name={item.name}
                id={item.id}
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </ListItem>
  );
}

const LensAdminReports: FC<LensAdminReportsProps> = (props) => {
  const navigate = useNavigate();
  const reports = useAppSelector(selectLookerReports);
  const status = useAppSelector(selectReportSliceStatus);
  const pendingOrIdle = status === RequestStatus.PENDING || status === RequestStatus.IDLE;

  return pendingOrIdle ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}>
      {[...Array(5)].map((_item, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={70}
          sx={{
            width: "100%",
          }}
        />
      ))}
    </Box>
  ) : (
    <List
      sx={{
        overflow: "auto",
        "& > *:not(:last-child)": {
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
        },
      }}>
      {reports?.folders.map((folder) => <ReportFolder key={folder.name} folder={folder} lensId={props.lensId} />)}

      {reports?.embedItems.map((item) => (
        <Box key={item.url} sx={{}}>
          <ReportListItem
            onClick={(reportId) => navigate(`/lens/${props.lensId}/report/${reportId}`)}
            name={item.name}
            id={item.id}
          />
        </Box>
      ))}
    </List>
  );
};

export default LensAdminReports;
